/**
 * Tell the HUMAN what write policy decided that they did not.
 *
 * The backend answers a create / update / transition with a `policy_adjustments`
 * array whenever it stored something other than what the caller would have
 * expected (`modules/software_factory/work_item_priority.py`). For an API or MCP
 * caller reading the response body that is the whole fix. A person clicking a
 * chip never sees a response body, so without this the override reaches them as a
 * number that changed on its own — which is the exact failure this whole contract
 * exists to end, one surface over.
 *
 * The path that makes it necessary is the detail page's TYPE chip. Re-typing an
 * item to `tech_debt` PUTs `{type: 'tech_debt'}` alone, and that payload is
 * deliberately routed to the silent clamp rather than the refusal (it names no
 * priority, so there is no submitted value to refuse). The priority badge next to
 * the type badge then drops to P3 with nothing said, which reads as a UI glitch.
 *
 * **Only an OVERRIDE is announced.** The two birth defaults — parent inheritance
 * and the customer-source default — fill in a value the user deliberately left
 * blank, so they violate no expectation and a toast on every child create would be
 * noise. They stay in the response for API callers, which is where they were asked
 * for. See ANNOUNCED_POLICIES.
 */
import i18n from '../../../i18n/config';
import { dispatchErrorToast } from '../../../shared/utils/globalErrorToasts';
import type { SfWorkItem, WorkItemPolicyAdjustment } from '../types';

/**
 * The policies worth interrupting someone over: the ones that DISCARDED a value
 * rather than supplying a missing one.
 */
const ANNOUNCED_POLICIES: ReadonlySet<WorkItemPolicyAdjustment['policy']> = new Set([
  'tech_debt_priority_floor',
]);

/**
 * Toast any override carried by a write response, then return the item unchanged.
 *
 * Shaped as a pass-through so it can wrap an API call's return value at the one
 * seam every caller funnels through (`workItemApi`'s write methods) rather than
 * being remembered at each of the four hooks — a clamp installed on three of four
 * seams reads as done, which is the mistake the backend half of this contract
 * already made once.
 *
 * It goes through `dispatchErrorToast(..., 'info')`, the same decoupled custom
 * event the global mutation handlers use, so this module imports no React and no
 * notification context. `info`, not `error`: the write succeeded, and a red toast
 * would contradict the sentence it carries.
 */
export function announcePolicyAdjustments<T extends SfWorkItem>(item: T): T {
  for (const adjustment of item.policy_adjustments ?? []) {
    if (!ANNOUNCED_POLICIES.has(adjustment.policy)) continue;
    dispatchErrorToast(
      i18n.t(`softwareFactory:policyAdjustments.${adjustment.policy}`, {
        applied: i18n.t(`softwareFactory:priorities.${adjustment.applied}`),
      }),
      'info',
    );
  }
  return item;
}
